/**
 * Logs de Acesso — Gestor Master visualiza todos os acessos e ações
 * administrativas do sistema (login, criação/bloqueio/exclusão de usuários,
 * reset de senhas, emissão manual de certificados, etc).
 */
import { useEffect, useState, useMemo } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RefreshCcw, LogIn, LogOut, ShieldOff, Shield, KeyRound, UserPlus,
  UserX, RotateCcw, FileText, Award, Wrench, Search, Filter, FilterX,
} from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  login_success: { label: "Login bem-sucedido", icon: LogIn, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  login_failed: { label: "Falha de login", icon: LogOut, color: "bg-rose-100 text-rose-800 border-rose-200" },
  staff_user_create: { label: "Usuário criado", icon: UserPlus, color: "bg-sky-100 text-sky-800 border-sky-200" },
  staff_user_block: { label: "Conta bloqueada", icon: ShieldOff, color: "bg-amber-100 text-amber-800 border-amber-200" },
  staff_user_unblock: { label: "Conta desbloqueada", icon: Shield, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  staff_user_soft_delete: { label: "Conta desativada", icon: UserX, color: "bg-rose-100 text-rose-800 border-rose-200" },
  staff_user_restore: { label: "Conta restaurada", icon: RotateCcw, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  staff_user_reset_password: { label: "Senha resetada (admin)", icon: KeyRound, color: "bg-purple-100 text-purple-800 border-purple-200" },
  admin_set_password: { label: "Senha definida (admin)", icon: KeyRound, color: "bg-purple-100 text-purple-800 border-purple-200" },
  self_change_password: { label: "Trocou própria senha", icon: KeyRound, color: "bg-sky-100 text-sky-800 border-sky-200" },
  survey_template_create: { label: "Template de pesquisa criado", icon: FileText, color: "bg-violet-100 text-violet-800 border-violet-200" },
  survey_template_update: { label: "Template de pesquisa editado", icon: FileText, color: "bg-violet-100 text-violet-800 border-violet-200" },
  survey_template_delete: { label: "Template de pesquisa removido", icon: FileText, color: "bg-violet-100 text-violet-800 border-violet-200" },
  certificate_manual_issue: { label: "Certificado emitido manualmente", icon: Award, color: "bg-amber-100 text-amber-800 border-amber-200" },
  attendance_indexes_repair: { label: "Reparo de índices", icon: Wrench, color: "bg-gray-100 text-gray-800 border-gray-200" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
};

export default function AdminLogsAcesso() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    const params = { days, limit: 500 };
    if (actionFilter !== "all") params.action = actionFilter;
    api.get("/admin/access-logs", { params })
      .then((r) => setLogs(r.data?.logs || []))
      .catch(() => toast.error("Erro ao carregar logs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, actionFilter]);

  const filtered = useMemo(() => {
    if (!search) return logs;
    const q = search.toLowerCase();
    return logs.filter((l) =>
      (l.actor_name || "").toLowerCase().includes(q) ||
      (l.actor_email || "").toLowerCase().includes(q) ||
      JSON.stringify(l.details || {}).toLowerCase().includes(q)
    );
  }, [logs, search]);

  return (
    <AdminLayout title="Logs de Acesso">
      <div className="space-y-4">
        {/* Filtros */}
        <Card className="rounded-xl">
          <CardContent className="p-3">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
              <div className="col-span-1 sm:col-span-2">
                <label className="text-[11px] uppercase font-medium text-muted-foreground tracking-wider">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    className="pl-7 h-8 text-sm"
                    placeholder="Nome, e-mail ou detalhes…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="logs-search"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase font-medium text-muted-foreground tracking-wider">Tipo de evento</label>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger className="h-8" data-testid="logs-action-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos eventos</SelectItem>
                    {Object.entries(ACTION_META).map(([k, m]) => (
                      <SelectItem key={k} value={k} className="text-xs">{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase font-medium text-muted-foreground tracking-wider">Período</label>
                <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                  <SelectTrigger className="h-8" data-testid="logs-days"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Últimas 24h</SelectItem>
                    <SelectItem value="7">Últimos 7 dias</SelectItem>
                    <SelectItem value="30">Últimos 30 dias</SelectItem>
                    <SelectItem value="90">Últimos 90 dias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-[11px] text-muted-foreground">
                Mostrando <strong>{filtered.length}</strong> de {logs.length} registros
              </p>
              <Button size="sm" variant="ghost" onClick={load}>
                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Atualizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabela */}
        <Card className="rounded-xl">
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="logs-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Data/Hora</TableHead>
                  <TableHead>Evento</TableHead>
                  <TableHead>Quem fez</TableHead>
                  <TableHead>Detalhes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground py-8">Carregando…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-10">Nenhum log encontrado para os filtros aplicados.</TableCell></TableRow>
                ) : (
                  filtered.map((l) => {
                    const meta = ACTION_META[l.action] || { label: l.action, icon: FileText, color: "bg-gray-100 text-gray-700 border-gray-200" };
                    const Icon = meta.icon;
                    return (
                      <TableRow key={l.id} data-testid={`log-${l.id}`}>
                        <TableCell className="text-[11px] font-mono whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${meta.color} text-[10px] gap-1`}>
                            <Icon className="w-3 h-3" />
                            {meta.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {l.actor_name}
                            {l.actor_deleted && <Badge variant="outline" className="ml-1 text-[9px] bg-gray-100">DESATIVADO</Badge>}
                          </div>
                          {l.actor_email && <p className="text-[10px] text-muted-foreground">{l.actor_email}</p>}
                          {l.actor_role && <Badge variant="outline" className="text-[9px] mt-0.5 capitalize">{l.actor_role.replace("_", " ")}</Badge>}
                        </TableCell>
                        <TableCell className="text-[11px] text-muted-foreground max-w-[300px]">
                          {l.details && Object.keys(l.details).length > 0 ? (
                            <div className="space-y-0.5">
                              {Object.entries(l.details).slice(0, 4).map(([k, v]) => (
                                <div key={k}>
                                  <span className="font-medium">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="opacity-50">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
